<script>
/**
 * Makes a drivers occupancy table fill the remaining height of its host container.
 *
 * Required in the host component:
 * - ref="occupancyToolbar" on the element rendered above the table
 * - tableHeightGutter (data, optional): extra vertical space reserved below the table
 *
 * The parent passes contentHeight when the table lives in the resizable dialog.
 * Without it the table falls back to the standalone view or legacy fixed height.
 */
const MIN_TABLE_HEIGHT = 160
const FALLBACK_TABLE_HEIGHT = '360px'
const FULL_VIEW_TABLE_HEIGHT = 'calc(100vh - 140px)'

export default {
  props: {
    contentHeight: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    occupancyToolbarHeight: 0,
    occupancyContainerHeight: 0,
    occupancyToolbarObserver: null
  }),
  computed: {
    isFullViewModeEnabled() {
      return this.$store.state.core.fullViewMode
    },
    tableHeight() {
      if (this.isFullViewModeEnabled) return FULL_VIEW_TABLE_HEIGHT

      const containerHeight = this.occupancyContainerHeight || this.contentHeight
      if (!containerHeight) return FALLBACK_TABLE_HEIGHT

      const reserved = this.occupancyToolbarHeight + (this.tableHeightGutter || 0)

      return `${Math.max(MIN_TABLE_HEIGHT, containerHeight - reserved)}px`
    }
  },
  watch: {
    contentHeight() {
      this.$nextTick(this.measureOccupancyLayout)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.measureOccupancyLayout()
      this.observeOccupancyLayout()
    })
  },
  beforeDestroy() {
    this.unobserveOccupancyLayout()
  },
  methods: {
    measureOccupancyLayout() {
      this.occupancyToolbarHeight = this.$refs.occupancyToolbar?.offsetHeight ?? 0
      this.occupancyContainerHeight = this.$el?.nodeType === 1 ? this.$el.clientHeight : 0
    },
    observeOccupancyLayout() {
      this.unobserveOccupancyLayout()

      if (typeof ResizeObserver === 'undefined') return

      this.occupancyToolbarObserver = new ResizeObserver(() => {
        this.measureOccupancyLayout()
      })

      if (this.$el?.nodeType === 1) {
        this.occupancyToolbarObserver.observe(this.$el)
      }

      const toolbar = this.$refs.occupancyToolbar
      if (toolbar) {
        this.occupancyToolbarObserver.observe(toolbar)
      }
    },
    unobserveOccupancyLayout() {
      if (!this.occupancyToolbarObserver) return

      this.occupancyToolbarObserver.disconnect()
      this.occupancyToolbarObserver = null
    }
  }
}
</script>
